import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";
import { zUsdCents, zUsdDollars, zUsdMicros } from "../currency";
import { DEFAULT_EXCHANGE_AGENT_USAGE_FEE } from "../team";

export enum BillingType {
  MARKETING_SMS = "marketing-sms",
  MARKETING_EMAIL = "marketing-email",
  ORDER_TRACKING = "order-tracking",
  RETURN_AI_EXCHANGE = "return-ai-exchange",
  RETURNS = "returns",
  CHECKOUT_OPTIMIZATION = "checkout-optimization",
  AEO = "aeo",
  IMS = "ims",
  IMS_FORECASTING = "ims-forecasting",
  RECOVER = "recover",
  DISPUTES = "disputes",
}

const baseBillingWindowSchema = z.object({
  _id: zExt.objectId(),
  teamId: zExt.objectId(),
  startsAt: z.coerce.date(),
  endsAt: z.coerce.date(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  type: createEnum(BillingType, { enumName: "BillingType" }),
});
export type CreateBillingWindow<
  T extends z.infer<typeof baseBillingWindowSchema>,
> = Omit<T, "_id" | "createdAt" | "updatedAt">;

export type MarketingSmsBillingWindow = z.infer<
  typeof marketingSmsBillingWindowSchema
>;
export const marketingSmsBillingWindowSchema = baseBillingWindowSchema.extend({
  type: z.literal(BillingType.MARKETING_SMS),

  // Credits and pricing for SMS/MMS
  includedSmsCredits: z.number(),
  overageSmsPriceMicros: zUsdMicros,
  overageMmsPriceMicros: zUsdMicros,

  ignoreCarrierFees: z.boolean(),
});

export type MarketingEmailBillingWindow = z.infer<
  typeof marketingEmailBillingWindowSchema
>;
export const marketingEmailBillingWindowSchema = baseBillingWindowSchema.extend(
  {
    type: z.literal(BillingType.MARKETING_EMAIL),

    // Credits and pricing for Email
    includedEmailCredits: z.number(),
    overagePriceMicros: zUsdMicros,
  },
);

export type OrderTrackingBillingWindow = z.infer<
  typeof orderTrackingBillingWindowSchema
>;
export const orderTrackingBillingWindowSchema = baseBillingWindowSchema.extend({
  type: z.literal(BillingType.ORDER_TRACKING),

  // Flags
  billPerOrderEnabled: z.boolean(),
  billPerSmsEnabled: z.boolean(),
  billPerEmailEnabled: z.boolean(),

  // Credits and pricing for Order Tracking
  includedOrders: z.number(),
  overagePricePerOrderMicros: zUsdMicros,

  includedSmsCredits: z.number(),
  overagePricePerSmsMicros: zUsdMicros,
  overagePricePerMmsMicros: zUsdMicros,
  ignoreCarrierFees: z.boolean(),

  includedEmailCredits: z.number(),
  overagePricePerEmailMicros: zUsdMicros,
});

export const exchangeAgentBillingFeeType = ["flat", "percentage"] as const;

export type ReturnAiExchangeBillingWindow = z.infer<
  typeof returnAiExchangeBillingWindowSchema
>;
export const returnAiExchangeBillingWindowSchema =
  baseBillingWindowSchema.extend({
    type: z.literal(BillingType.RETURN_AI_EXCHANGE),

    // Pricing snapshot when billing window was created
    // Optional with default for backward compatibility with existing documents
    usageFeeAtCreation: zUsdDollars
      .nullish()
      .default(DEFAULT_EXCHANGE_AGENT_USAGE_FEE as z.infer<typeof zUsdDollars>),
    // Percentage fee snapshot when billing window was created
    // Optional with default for backward compatibility with existing documents
    usageFeePercentAtCreation: z.number().nullish().default(0),
    // Billing fee type snapshot when billing window was created
    // Optional with default for backward compatibility with existing documents
    billingFeeTypeAtCreation: z
      .enum(exchangeAgentBillingFeeType)
      .nullish()
      .default("flat"),
  });

export type ReturnsBillingWindow = z.infer<typeof returnsBillingWindowSchema>;
export const returnsBillingWindowSchema = baseBillingWindowSchema.extend({
  type: z.literal(BillingType.RETURNS),
});

export type AeoBillingWindow = z.infer<typeof aeoBillingWindowSchema>;
export const aeoBillingWindowSchema = baseBillingWindowSchema.extend({
  type: z.literal(BillingType.AEO),
});

export type ImsBillingWindow = z.infer<typeof imsBillingWindowSchema>;
export const imsBillingWindowSchema = baseBillingWindowSchema.extend({
  type: z.literal(BillingType.IMS),
});

export type ImsForecastingBillingWindow = z.infer<
  typeof imsForecastingBillingWindowSchema
>;
export const imsForecastingBillingWindowSchema = baseBillingWindowSchema.extend(
  { type: z.literal(BillingType.IMS_FORECASTING) },
);

export const checkoutOptimizationBillingWindowSchema =
  baseBillingWindowSchema.extend({
    type: z.literal(BillingType.CHECKOUT_OPTIMIZATION),
    pricePerOrderInCents: zUsdCents,
  });
export type CheckoutOptimizationBillingWindow = z.infer<
  typeof checkoutOptimizationBillingWindowSchema
>;

export type RecoverBillingWindow = z.infer<typeof recoverBillingWindowSchema>;
export const recoverBillingWindowSchema = baseBillingWindowSchema.extend({
  type: z.literal(BillingType.RECOVER),
  revenueSharePercentage: z.number(),
  billPerSmsEnabled: z.boolean(),
  includedSmsCredits: z.number(),
  overagePricePerSmsMicros: zUsdMicros,
  overagePricePerMmsMicros: zUsdMicros,
  ignoreCarrierFees: z.boolean(),
});

export type DisputesBillingWindow = z.infer<typeof disputesBillingWindowSchema>;
export const disputesBillingWindowSchema = baseBillingWindowSchema.extend({
  type: z.literal(BillingType.DISPUTES),
  minFeeDollars: z.number(),
  feePercentage: z.number(),
});

export const billingWindowConfigSchema = z.union([
  checkoutOptimizationBillingWindowSchema,
  marketingSmsBillingWindowSchema,
  marketingEmailBillingWindowSchema,
  orderTrackingBillingWindowSchema,
  returnAiExchangeBillingWindowSchema,
  returnsBillingWindowSchema,
  aeoBillingWindowSchema,
  imsBillingWindowSchema,
  imsForecastingBillingWindowSchema,
  recoverBillingWindowSchema,
  disputesBillingWindowSchema,
]);

export type BillingWindowConfig = z.infer<typeof billingWindowConfigSchema>;
